import { useEffect } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import { useMapStore } from '../../../stores/mapStore';
import { getTileRoot } from '../../../lib/env';
import type { LayerSettings } from '../LayerSettingsPanel';
import {
  OBSERVED_WEATHER_LAYER_DEFS,
  OBSERVED_WEATHER_LAYER_MAX_ZOOM,
  OBSERVED_WEATHER_TILE_MAX_ZOOM,
  OBSERVED_WEATHER_TILE_MIN_ZOOM,
  WIND_ARROW_MAX_ZOOM,
  WIND_ARROW_MIN_ZOOM,
  WIND_DIRECTION_DEF,
  WIND_SPEED_DEF,
  observedWeatherFillLayerId,
  observedWeatherPeriodTilesUrl,
  observedWeatherSourceId,
  observedWeatherWindArrowLayerId,
  type ObservedWeatherLayerDef,
} from '../../../lib/observedWeather';
import { WIND_ARROW_ICON_ID, ensureWindArrowIcon } from '../icons/windArrowIcon';
import { fetchTiledPeriodManifest } from '../../../lib/tilePeriods';

type ObservedWeatherSettings = Pick<
  LayerSettings,
  | 'observedWeatherTemp'
  | 'observedWeatherWind'
  | 'observedWeatherWindDirection'
  | 'observedWeatherPrecip'
  | 'observedWeatherTempPeriods'
  | 'observedWeatherWindPeriods'
  | 'observedWeatherWindDirectionPeriods'
  | 'observedWeatherPrecipPeriods'
>;

function tilesBase(): string {
  return getTileRoot();
}

function periodsForDef(settings: ObservedWeatherSettings, def: ObservedWeatherLayerDef): string[] {
  return settings[def.periodSettingsKey];
}

function sourceMinZoom(map: MapboxMap, sourceId: string): number | undefined {
  const spec = map.getStyle()?.sources?.[sourceId];
  if (!spec || spec.type !== 'vector') return undefined;
  return typeof spec.minzoom === 'number' ? spec.minzoom : undefined;
}

function insertBeforeId(map: MapboxMap): string | undefined {
  if (map.getLayer('ghana-flood-exposure-fill')) return 'ghana-flood-exposure-fill';
  if (map.getLayer('ghana-water')) return 'ghana-water';
  return undefined;
}

function ensureVectorSource(
  map: MapboxMap,
  sourceId: string,
  tileLayer: string,
  period: string,
) {
  const tileRoot = tilesBase();
  if (!tileRoot) return;

  const expectedMinZoom = OBSERVED_WEATHER_TILE_MIN_ZOOM;
  const existingMinZoom = sourceMinZoom(map, sourceId);
  if (existingMinZoom !== undefined && existingMinZoom !== expectedMinZoom) {
    for (const layer of map.getStyle()?.layers ?? []) {
      if (layer.source === sourceId && map.getLayer(layer.id)) {
        map.removeLayer(layer.id);
      }
    }
    if (map.getSource(sourceId)) map.removeSource(sourceId);
  }

  if (!map.getSource(sourceId)) {
    map.addSource(sourceId, {
      type: 'vector',
      tiles: [observedWeatherPeriodTilesUrl(tileRoot, tileLayer, period)],
      minzoom: expectedMinZoom,
      maxzoom: OBSERVED_WEATHER_TILE_MAX_ZOOM,
    });
  }
}

function ensurePeriodFillLayer(map: MapboxMap, def: ObservedWeatherLayerDef, period: string) {
  const sourceId = observedWeatherSourceId(def, period);
  const fillLayerId = observedWeatherFillLayerId(def, period);
  ensureVectorSource(map, sourceId, def.tileLayer, period);

  if (!map.getLayer(fillLayerId)) {
    map.addLayer(
      {
        id: fillLayerId,
        type: 'fill',
        source: sourceId,
        'source-layer': def.sourceLayer,
        minzoom: OBSERVED_WEATHER_TILE_MIN_ZOOM,
        maxzoom: OBSERVED_WEATHER_LAYER_MAX_ZOOM,
        layout: { visibility: 'visible' },
        paint: def.paint,
      },
      insertBeforeId(map),
    );
  }

  map.setLayerZoomRange(fillLayerId, OBSERVED_WEATHER_TILE_MIN_ZOOM, OBSERVED_WEATHER_LAYER_MAX_ZOOM);
  map.setLayoutProperty(fillLayerId, 'visibility', 'visible');
}

function ensureWindArrowLayer(map: MapboxMap, period: string, directionPeriods?: Set<string>) {
  const def = WIND_DIRECTION_DEF;
  const sourceId = observedWeatherSourceId(def, period);
  const arrowLayerId = observedWeatherWindArrowLayerId(period);

  if (directionPeriods && !directionPeriods.has(period)) {
    removeWindArrowLayer(map, period);
    return;
  }

  ensureVectorSource(map, sourceId, def.tileLayer, period);

  if (!map.hasImage(WIND_ARROW_ICON_ID)) return;

  if (map.getLayer(arrowLayerId)) {
    map.setLayerZoomRange(arrowLayerId, WIND_ARROW_MIN_ZOOM, WIND_ARROW_MAX_ZOOM);
    map.setLayoutProperty(arrowLayerId, 'visibility', 'visible');
    return;
  }

  map.addLayer(
    {
      id: arrowLayerId,
      type: 'symbol',
      source: sourceId,
      'source-layer': def.sourceLayer,
      minzoom: WIND_ARROW_MIN_ZOOM,
      maxzoom: WIND_ARROW_MAX_ZOOM,
      filter: ['has', 'value'],
      layout: {
        'symbol-placement': 'point',
        'icon-image': WIND_ARROW_ICON_ID,
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          6,
          0.5,
          8,
          0.56,
          10,
          0.58,
          12,
          0.72,
          14,
          0.92,
          16,
          1.08,
          18,
          1.2,
        ],
        'icon-rotate': ['+', ['coalesce', ['get', 'value'], 0], 180],
        'icon-rotation-alignment': 'map',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        visibility: 'visible',
      },
      paint: {
        'icon-opacity': 0.94,
      },
    },
    insertBeforeId(map),
  );

  const fillLayerId = observedWeatherFillLayerId(WIND_SPEED_DEF, period);
  if (map.getLayer(fillLayerId)) {
    try {
      map.moveLayer(arrowLayerId, insertBeforeId(map));
    } catch {
      // layer order best-effort
    }
  }

  map.setLayerZoomRange(arrowLayerId, WIND_ARROW_MIN_ZOOM, WIND_ARROW_MAX_ZOOM);
  map.setLayoutProperty(arrowLayerId, 'visibility', 'visible');
}

function removePeriodFillLayer(map: MapboxMap, def: ObservedWeatherLayerDef, period: string) {
  const sourceId = observedWeatherSourceId(def, period);
  const fillLayerId = observedWeatherFillLayerId(def, period);
  if (map.getLayer(fillLayerId)) map.removeLayer(fillLayerId);
  if (map.getSource(sourceId)) map.removeSource(sourceId);
}

function removeWindArrowLayer(map: MapboxMap, period: string) {
  const def = WIND_DIRECTION_DEF;
  const sourceId = observedWeatherSourceId(def, period);
  const arrowLayerId = observedWeatherWindArrowLayerId(period);
  if (map.getLayer(arrowLayerId)) map.removeLayer(arrowLayerId);
  if (map.getSource(sourceId)) map.removeSource(sourceId);
}

function removeLegacyWindDirectionFill(map: MapboxMap, period: string) {
  const fillLayerId = observedWeatherFillLayerId(WIND_DIRECTION_DEF, period);
  const sourceId = observedWeatherSourceId(WIND_DIRECTION_DEF, period);
  if (map.getLayer(fillLayerId)) map.removeLayer(fillLayerId);
  if (map.getSource(sourceId)) map.removeSource(sourceId);
}

function hideLegacySingleLayers(map: MapboxMap, def: ObservedWeatherLayerDef) {
  const legacyFill = def.fillLayerIdPrefix.replace(/-$/, '');
  if (map.getLayer(legacyFill)) {
    map.setLayoutProperty(legacyFill, 'visibility', 'none');
  }
}

function syncWindLayers(
  map: MapboxMap,
  enabled: boolean,
  selectedPeriods: string[],
  directionPeriods?: Set<string>,
) {
  const active = enabled ? new Set(selectedPeriods) : new Set<string>();
  const style = map.getStyle();

  if (style?.layers) {
    for (const layer of style.layers) {
      const id = layer.id;
      if (id.startsWith(WIND_SPEED_DEF.fillLayerIdPrefix)) {
        const period = id.slice(WIND_SPEED_DEF.fillLayerIdPrefix.length);
        if (!active.has(period)) {
          removePeriodFillLayer(map, WIND_SPEED_DEF, period);
          removeWindArrowLayer(map, period);
          removeLegacyWindDirectionFill(map, period);
        }
      }
      if (id.startsWith('ghana-observed-weather-wind-arrow-')) {
        const period = id.slice('ghana-observed-weather-wind-arrow-'.length);
        if (!active.has(period)) removeWindArrowLayer(map, period);
      }
      if (id.startsWith(WIND_DIRECTION_DEF.fillLayerIdPrefix)) {
        const period = id.slice(WIND_DIRECTION_DEF.fillLayerIdPrefix.length);
        removeLegacyWindDirectionFill(map, period);
      }
    }
  }

  if (!enabled) return;

  for (const period of selectedPeriods) {
    ensurePeriodFillLayer(map, WIND_SPEED_DEF, period);
    ensureWindArrowLayer(map, period, directionPeriods);
    removeLegacyWindDirectionFill(map, period);
  }
}

function syncVariableLayers(
  map: MapboxMap,
  def: ObservedWeatherLayerDef,
  enabled: boolean,
  selectedPeriods: string[],
  directionPeriods?: Set<string>,
) {
  if (def.settingsKey === 'observedWeatherWind') {
    syncWindLayers(map, enabled, selectedPeriods, directionPeriods);
    return;
  }
  if (def.settingsKey === 'observedWeatherWindDirection') {
    return;
  }

  const active = enabled ? new Set(selectedPeriods) : new Set<string>();
  const style = map.getStyle();

  if (style?.layers) {
    for (const layer of style.layers) {
      const id = layer.id;
      if (!id.startsWith(def.fillLayerIdPrefix)) continue;
      const period = id.slice(def.fillLayerIdPrefix.length);
      if (!active.has(period)) removePeriodFillLayer(map, def, period);
    }
  }

  if (!enabled) return;

  for (const period of selectedPeriods) {
    ensurePeriodFillLayer(map, def, period);
  }
}

function syncObservedWeatherLayers(
  map: MapboxMap,
  settings: ObservedWeatherSettings,
  directionPeriods?: Set<string>,
) {
  for (const def of OBSERVED_WEATHER_LAYER_DEFS) {
    hideLegacySingleLayers(map, def);
    syncVariableLayers(map, def, settings[def.settingsKey], periodsForDef(settings, def), directionPeriods);
  }
}

/** Observed weather — per-period fills; wind pairs speed fill with direction arrows from z6. */
export function useObservedWeatherLayers(
  mapRef: React.MutableRefObject<MapboxMap | null>,
  settings: ObservedWeatherSettings,
) {
  const { isMapLoaded } = useMapStore();
  const syncKey = OBSERVED_WEATHER_LAYER_DEFS.map((def) => {
    if (def.settingsKey === 'observedWeatherWindDirection') {
      return '';
    }
    const enabled = settings[def.settingsKey];
    const periods = periodsForDef(settings, def).slice().sort().join(',');
    return `${def.settingsKey}:${enabled}:${periods}`;
  }).join('|');

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapLoaded) return;

    const apply = () => {
      const activeMap = mapRef.current;
      if (!activeMap?.isStyleLoaded()) return;
      void ensureWindArrowIcon(activeMap)
        .then(async () => {
          let directionPeriods: Set<string> | undefined;
          if (settings.observedWeatherWind && settings.observedWeatherWindPeriods.length > 0) {
            const { periods } = await fetchTiledPeriodManifest(WIND_DIRECTION_DEF.tileLayer);
            directionPeriods = new Set(periods);
          }
          syncObservedWeatherLayers(activeMap, settings, directionPeriods);
        })
        .catch((err) => {
          console.warn('[observed-weather] wind arrow icon failed to load', err);
          syncObservedWeatherLayers(activeMap, settings);
        });
    };

    apply();
    map.on('load', apply);
    map.on('style.load', apply);
    return () => {
      map.off('load', apply);
      map.off('style.load', apply);
    };
  }, [mapRef, isMapLoaded, syncKey, settings]);
}
